'use client';

import type { Product, ProductTimeSlot } from "@/lib/TicketApi";
import { shopVar } from "@/lib/shopStyles";
import { timeSlotDay, timeSlotTimeRange, timeSlotsSpanMultipleDays } from "@/lib/timeSlot";
import { useCart } from "@/providers/CartProvider";
import { useLanguage } from "@/providers/LanguageProvider";
import QuantitySelectButton from "@/components/QuantitySelectButton";
import * as m from "@/paraglide/messages.js"

interface TimeSlotListProps {
    product: Product;
    /** Max quantity for the product as a whole — the slots share it. */
    limit: number;
}

/**
 * FAS-1053 — an entrance product sold per time slot: one row per slot, each row driving its own
 * cart line, in place of the product's single stepper. min/max/step stay product-wide (that is
 * how the API enforces them), so a row's plus button also stops at the product's limit.
 */
export default function TimeSlotList({product, limit}: TimeSlotListProps) {
    const {
        setProductQuantity,
        selectedQuantity,
        selectedQuantityForSlot,
        loading,
        isProductQuantityIncreaseDisabled,
        isProductQuantityIncreaseDisabledByShopUnlock,
    } = useCart();
    const { currentLanguage: lang } = useLanguage();

    const slots = product.timeSlots ?? [];
    const showDay = timeSlotsSpanMultipleDays(slots);
    const totalSelected = selectedQuantity(product.productId);
    const limitReached = totalSelected >= limit
        || isProductQuantityIncreaseDisabled(product.productId)
        || isProductQuantityIncreaseDisabledByShopUnlock(product.productId);

    const setSlotQuantity = (slot: ProductTimeSlot, quantity: number) => {
        setProductQuantity(product.productId, quantity, { timeSlotId: slot.id });
    };

    const handleIncrease = (slot: ProductTimeSlot, quantity: number) => {
        if (limitReached) {
            return;
        }
        setSlotQuantity(slot, quantity === 0 && product.minQuantity > 0
            ? product.minQuantity
            : quantity + product.stepQuantity);
    };

    const handleDecrease = (slot: ProductTimeSlot, quantity: number) => {
        if (quantity <= 0) {
            return;
        }
        const next = quantity - product.stepQuantity;
        if (next < product.minQuantity) {
            setSlotQuantity(slot, quantity === product.minQuantity ? 0 : product.minQuantity);
            return;
        }
        setSlotQuantity(slot, next);
    };

    return (
        <ul className="flex flex-col">
            {slots.map(slot => {
                const quantity = selectedQuantityForSlot(product.productId, slot.id);
                const unavailable = slot.closed || slot.inventoryStatus === 'sold_out';
                const allReserved = slot.inventoryStatus === 'all_reserved';

                return (
                    <li
                        key={slot.id}
                        className="flex items-center justify-between gap-2 py-2 border-b border-dashed last:border-b-0"
                        style={{ borderColor: shopVar('--shop-borderColor') }}
                    >
                        <div className={`flex flex-col ${unavailable && quantity === 0 ? 'opacity-60' : ''}`}>
                            {showDay && (
                                <span className="text-xs tracking-tight" style={{ color: shopVar('--shop-textColor') }}>
                                    {timeSlotDay(slot, lang)}
                                </span>
                            )}
                            <span className="text-sm font-semibold" style={{ color: shopVar('--shop-textColor') }}>
                                {timeSlotTimeRange(slot, lang)}
                            </span>
                        </div>

                        {unavailable && quantity === 0 ? (
                            <span className="text-sm font-light opacity-60" style={{ color: shopVar('--shop-textColor') }}>
                                {slot.closed ? m.time_slot_closed() : m.product_sold_out()}
                            </span>
                        ) : allReserved && quantity === 0 ? (
                            <span className="text-sm font-light opacity-60" style={{ color: shopVar('--shop-textColor') }}>
                                {m.product_all_reserved()}
                            </span>
                        ) : (
                            <QuantitySelectButton
                                quantity={quantity}
                                limit={limit}
                                loading={loading}
                                increaseDisabled={limitReached}
                                onIncrease={() => handleIncrease(slot, quantity)}
                                onDecrease={() => handleDecrease(slot, quantity)}
                            />
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
